import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock, X } from "lucide-react";
import SignInForm from "./SignInForm";
import { useUserAuth } from "../lib/userAuth";

/**
 * SignInModal — popup version of /signin. Renders centered over the page
 * with a backdrop. Same form behavior as the dedicated /signin page (via
 * the shared <SignInForm>) so users can authenticate without losing
 * context.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   defaultMode: "login" | "signup"  (default "login")
 *   onSuccess: (account) => void     (optional — called after successful auth)
 *
 * iter 4.9 — credentials form extracted to <SignInForm>.
 */
export default function SignInModal({ open, onClose, defaultMode = "login", onSuccess }) {
  const { t } = useTranslation();
  const { signup, login } = useUserAuth();
  const [mode, setMode] = useState(defaultMode);
  // `formKey` lets us remount <SignInForm> when the modal reopens so
  // its internal state (email/password/magic-sent) resets cleanly.
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (!open) {
      setMode(defaultMode);
      setFormKey((k) => k + 1);
    }
  }, [open, defaultMode]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const onLogin = async ({ email, password }) => {
    const account = await login({ email, password });
    onSuccess?.(account);
    onClose?.();
  };

  // Modal-mode signup: single-step (no persona). Caller can prompt for
  // persona post-signup via PersonaPromptModal.
  const onSignupSubmit = async (creds) => {
    const account = await signup(creds);
    onSuccess?.(account);
    onClose?.();
  };

  const onMagicTrusted = () => {
    window.location.assign("/account?magic=trusted");
  };

  return (
    <div
      data-testid="signin-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signin-modal-title"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        background: "rgba(2, 6, 14, 0.92)",
        /* Iter255 — opaque backdrop; no blur-through behind modals */
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1.5rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="qt-card qt-grain"
        style={{
          width: "100%", maxWidth: 520,
          maxHeight: "calc(100vh - 3rem)", overflowY: "auto",
          border: "1px solid var(--qt-cyan)",
          position: "relative",
          animation: "qt-modal-in .18s ease-out",
        }}
      >
        <button
          data-testid="signin-modal-close"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute", top: 16, right: 16,
            background: "transparent", border: "1px solid var(--qt-border)",
            borderRadius: 6, padding: 6, color: "var(--qt-text-2)",
            cursor: "pointer", display: "flex", alignItems: "center",
          }}
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-3">
          <Lock size={16} style={{ color: "var(--qt-cyan)" }} />
          <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>
            {mode === "signup" ? t("auth.signup_eyebrow_step1", "CREATE A FREE ACCOUNT") : t("auth.signin_eyebrow", "SIGN IN TO QORTRACE")}
          </div>
        </div>
        <h2 id="signin-modal-title" className="qt-h2 text-2xl sm:text-3xl mt-2">
          {mode === "signup" ? t("auth.signup_title", "Welcome to QorTrace.") : t("auth.signin_title", "Welcome back.")}
        </h2>
        <p className="mt-2 text-sm" style={{ color: "var(--qt-text-2)" }}>
          {mode === "signup"
            ? t("auth.signup_subtitle", "Free accounts get one free preview scan per address. Upgrade for unlimited deep-dive analysis.")
            : t("auth.signin_subtitle", "Sign in to manage your plan and run unlimited scans on your tier.")}
        </p>

        <SignInForm
          key={formKey}
          testidPrefix="signin-modal"
          mode={mode}
          setMode={setMode}
          onLogin={onLogin}
          onSignupSubmit={onSignupSubmit}
          onMagicTrusted={onMagicTrusted}
          onForgotClick={onClose}
          onFreescanClick={onClose}
          autoFocusEmail
          signupSubmitLabel="Create account"
        />
      </div>

      <style>{`
        @keyframes qt-modal-in {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
      `}</style>
    </div>
  );
}
